import Layout from 'components/layout';

import React, { FC, useState } from 'react';

import { graphql } from 'gatsby';
import { Box, Heading, Icon, Text, Link, Container } from '@lucid/components-react';
import { getI18nData, I18nDataEdges, getEntityFields } from '@lucid/utils';
import {
  Drupal,
  Drupal_EntityQueryResult,
  Drupal_NodeFlexiArticleHub,
  Drupal_NodeAwards,
  Drupal_NodeNews,
  Drupal_NodePressReleases
} from 'generated/drupal-graphql';
import { mqMin, LucidTheme } from '@lucid/theme';

import News from 'components/flexi-articles/news/news';
import PressRelease from 'components/flexi-articles/press-releases/press-releases';
import FlexiStyledMultiSections from 'components/flexi/flexi-styled-multi-sections/flexi-styled-multi-sections';
import Awards from 'components/flexi-articles/awards/awards';
import FlourishedHeading from 'components/flourished-heading/flourished-heading';
import { TemplateProps } from 'types/types';
import { useMenuTopNavigation } from 'hooks/use-menu';

interface NewsroomDrupal extends Drupal {
  awards: Drupal_EntityQueryResult;
  newsroom: Drupal_EntityQueryResult;
  pressReleases: Drupal_EntityQueryResult;
  nodeById: Drupal_NodeFlexiArticleHub;
}

interface FlexiArticleHubProps extends TemplateProps {
  data: {
    drupal: NewsroomDrupal;
    i18nData: I18nDataEdges;
  };
}

const FlexiArticleHub: FC<FlexiArticleHubProps> = ({ data, pageContext, location }) => {
  const { langPrefix, pageType, nid, hideSsr, i18nKey } = pageContext;
  const {
    drupal: { pressReleases, newsroom, awards, nodeById },
    i18nData
  } = data;
  const i18n = getI18nData(i18nData, i18nKey);
  const {
    fieldHeader,
    fieldPressReleaseHeader,
    fieldAwardHeader,
    fieldFlexiPrefooterHeading,
    fieldFlexiPrefooterLink,
    fieldFlexiContact
  } = nodeById as Drupal_NodeFlexiArticleHub;
  const { entityMetatags, entityTranslations } = getEntityFields(nodeById);

  const [newsFilterHighlight, setNewsFilterHighlight] = useState('');
  const [prFilterHighlight, setPrFilterHighlight] = useState('');

  const ogNewsroomArticles = newsroom!.entities as Drupal_NodeNews[];
  const ogPressReleaseArticles = pressReleases!
    .entities as Drupal_NodePressReleases[];
  const ogAwardsArticles = awards!.entities as Drupal_NodeAwards[];

  const [newsroomArticles, setNewsroomArticles] = useState(ogNewsroomArticles);
  const [pressReleaseArticles, setPressReleaseArticles] = useState(
    ogPressReleaseArticles
  );
  const [awardsArticles] = useState(ogAwardsArticles);

  const filterArticles = (filterValue: string, filterType: string) => {
    if (filterType === 'pr') {
      let articles = ogPressReleaseArticles;
      if (filterValue !== '') {
        const newArticles = [] as Drupal_NodePressReleases[];
        articles.filter((item: any) => {
          item.fieldPrProduct.map((product: any) => {
            if (product === filterValue) {
              newArticles.push(item);
            }
          });
        });
        articles = newArticles;
      }
      setPressReleaseArticles(articles);
      setPrFilterHighlight(filterValue);
    } else {
      let articles = ogNewsroomArticles;
      if (filterValue !== '') {
        const filteredArticles = [] as Drupal_NodeNews[];
        articles.filter((item: any) => {
          item.fieldPrProduct.map((product: any) => {
            if (product === filterValue) {
              filteredArticles.push(item);
            }
          });
        });
        articles = filteredArticles;
      }
      setNewsroomArticles(articles);
      setNewsFilterHighlight(filterValue);
    }
  };

  const navbarMenu = useMenuTopNavigation(langPrefix);

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      navbar={{menu: navbarMenu}}
      nid={nid}
      pageType={pageType}
    >
      <Container
        as="main"
        css={(theme: LucidTheme) => ({
          background: theme.colors.white,
          overflow: 'hidden'
        })}
      >
        {newsroomArticles && (
          <Box>
            <Heading
              as="h1"
              m={0}
              my={6}
              textAlign="center"
              variant="COheadlineXS700"
            >
              {fieldHeader}
            </Heading>

            <Box
              css={(theme: LucidTheme) => ({
                display: 'flex',
                marginTop: theme.space[6],
                marginBottom: theme.space[6],
                justifyContent: 'center',
                '&:hover': {
                  cursor: 'pointer'
                }
              })}
            >
              <Text
                css={(theme: LucidTheme) => ({
                  marginRight: theme.space[3],
                  color: `${
                    newsFilterHighlight === '' ? theme.colors.blue[60] : ''
                    }`,
                  [mqMin.tabletLandscape]: {
                    marginRight: theme.space[6]
                  }
                })}
                onClick={() => filterArticles('', 'news')}
                variant="COlinkM500"
              >
                <Icon
                  css={(theme: LucidTheme) => ({
                    marginRight: theme.space[2],
                    fontSize: theme.fontSizes[2]
                  })}
                  type="ArrowRight"
                />
                {i18n('mktg-newsroom-all')}
              </Text>
              <Text
                css={(theme: LucidTheme) => ({
                  marginRight: theme.space[2],
                  color: `${
                    newsFilterHighlight === 'lucid'
                      ? theme.colors.blue[60]
                      : ''
                    }`,
                  [mqMin.tabletLandscape]: {
                    marginRight: theme.space[6]
                  }
                })}
                onClick={() => filterArticles('lucid', 'news')}
                variant="COlinkM500"
              >
                <Icon
                  css={(theme: LucidTheme) => ({
                    marginRight: theme.space[2],
                    fontSize: theme.fontSizes[2]
                  })}
                  type="ArrowRight"
                />
                Lucid
              </Text>
              <Text
                css={(theme: LucidTheme) => ({
                  marginRight: theme.space[2],
                  color: `${
                    newsFilterHighlight === 'lucidchart'
                      ? theme.colors.blue[60]
                      : ''
                    }`,
                  [mqMin.tabletLandscape]: {
                    marginRight: theme.space[6]
                  }
                })}
                onClick={() => filterArticles('lucidchart', 'news')}
                variant="COlinkM500"
              >
                <Icon
                  css={(theme: LucidTheme) => ({
                    marginRight: theme.space[2],
                    fontSize: theme.fontSizes[2]
                  })}
                  type="ArrowRight"
                />
                Lucidchart
              </Text>
              <Text
                css={(theme: LucidTheme) => ({
                  marginRight: theme.space[2],
                  color: `${
                    newsFilterHighlight === 'lucidspark'
                      ? theme.colors.blue[60]
                      : ''
                    }`,
                  [mqMin.tabletLandscape]: {
                    marginRight: theme.space[6]
                  }
                })}
                onClick={() => filterArticles('lucidspark', 'news')}
                variant="COlinkM500"
              >
                <Icon
                  css={(theme: LucidTheme) => ({
                    marginRight: theme.space[2],
                    fontSize: theme.fontSizes[2]
                  })}
                  type="ArrowRight"
                />
                Lucidspark
              </Text>
            </Box>

            <Box
              css={(theme: LucidTheme) => ({
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                marginTop: theme.space[6],
                marginBottom: theme.space[6]
              })}
              gridColumn="1 / -1"
            >
              {newsroomArticles.length > 0 ? (
                <News articles={newsroomArticles} />
              ) :
                (
                  <Text variant="CObody">
                    {i18n('mktg_resource_center_no_results')}
                  </Text>
                )}
            </Box>
          </Box>
        )}
        {pressReleaseArticles && (
          <Box
            css={(theme: LucidTheme) => ({
              flex: '0 1 100%',
              maxWidth: '100%',
              [mqMin.tabletLandscape]: {
                marginLeft: theme.space[4],
                marginRight: theme.space[4]
              }
            })}
          >
            {fieldPressReleaseHeader && (
              <FlourishedHeading
                as="h2"
                css={(theme: LucidTheme) => ({
                  marginBottom: theme.space[5],
                  [mqMin.tabletLandscape]: {
                    marginBottom: theme.space[9],
                    flex: '0 1 45%',
                    maxWidth: '45%'
                  }
                })}
                variant="COsubheadL700"
              >
                {fieldPressReleaseHeader}
              </FlourishedHeading>
            )}
            <Box
              css={(theme: LucidTheme) => ({
                display: 'flex',
                flexWrap: 'wrap',
                marginTop: theme.space[3],
                marginBottom: theme.space[3],
                [mqMin.tabletLandscape]: {
                  marginTop: theme.space[6],
                  marginBottom: theme.space[6]
                },
                '&:hover': {
                  cursor: 'pointer'
                }
              })}
            >
              <Text
                css={(theme: LucidTheme) => ({
                  marginRight: theme.space[4],
                  color: `${
                    prFilterHighlight === '' ? theme.colors.blue[60] : ''
                    }`,
                  [mqMin.tabletLandscape]: {
                    marginRight: theme.space[6]
                  }
                })}
                onClick={() => filterArticles('', 'pr')}
                variant="COlinkM500"
              >
                <Icon
                  css={(theme: LucidTheme) => ({
                    marginRight: theme.space[2],
                    fontSize: theme.fontSizes[2]
                  })}
                  type="ArrowRight"
                />
                {i18n('mktg-newsroom-all')}
              </Text>
              <Text
                css={(theme: LucidTheme) => ({
                  marginRight: theme.space[4],
                  color: `${
                    prFilterHighlight === 'lucid'
                      ? theme.colors.blue[60]
                      : ''
                    }`,
                  [mqMin.tabletLandscape]: {
                    marginRight: theme.space[6]
                  }
                })}
                onClick={() => filterArticles('lucid', 'pr')}
                variant="COlinkM500"
              >
                <Icon
                  css={(theme: LucidTheme) => ({
                    marginRight: theme.space[2],
                    fontSize: theme.fontSizes[2]
                  })}
                  type="ArrowRight"
                />
                Lucid
              </Text>
              <Text
                css={(theme: LucidTheme) => ({
                  marginRight: theme.space[4],
                  color: `${
                    prFilterHighlight === 'lucidchart'
                      ? theme.colors.blue[60]
                      : ''
                    }`,
                  [mqMin.tabletLandscape]: {
                    marginRight: theme.space[6]
                  }
                })}
                onClick={() => filterArticles('lucidchart', 'pr')}
                variant="COlinkM500"
              >
                <Icon
                  css={(theme: LucidTheme) => ({
                    marginRight: theme.space[2],
                    fontSize: theme.fontSizes[2]
                  })}
                  type="ArrowRight"
                />
                Lucidchart
              </Text>
              <Text
                css={(theme: LucidTheme) => ({
                  marginRight: theme.space[4],
                  color: `${
                    prFilterHighlight === 'lucidspark'
                      ? theme.colors.blue[60]
                      : ''
                    }`,
                  [mqMin.tabletLandscape]: {
                    marginRight: theme.space[6]
                  }
                })}
                onClick={() => filterArticles('lucidspark', 'pr')}
                variant="COlinkM500"
              >
                <Icon
                  css={(theme: LucidTheme) => ({
                    marginRight: theme.space[2],
                    fontSize: theme.fontSizes[2]
                  })}
                  type="ArrowRight"
                />
                Lucidspark
              </Text>
            </Box>
            <Box
              css={(theme: LucidTheme) => ({
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                marginTop: theme.space[6],
                marginBottom: theme.space[6]
              })}
              gridColumn="1 / -1"
            >
              {pressReleaseArticles.length > 0 ? (
                <PressRelease
                  articles={pressReleaseArticles}
                  showLess={i18n('mktg_show_less')}
                  showMore={i18n('mktg_show_more')}
                />
              ) :
                (
                  <Text variant="CObody">
                    {i18n('mktg-resource-center-no-results')}
                  </Text>
                )}
            </Box>
          </Box>
        )}
        <Box
          css={(theme: LucidTheme) => ({
            flex: '0 1 100%',
            maxWidth: '100%',
            [mqMin.tabletLandscape]: {
              marginLeft: theme.space[4],
              marginRight: theme.space[4]
            }
          })}
        >
          {fieldAwardHeader && (
            <FlourishedHeading
              as="h2"
              css={(theme: LucidTheme) => ({
                marginBottom: theme.space[9],
                [mqMin.tabletLandscape]: {
                  flex: '0 1 45%',
                  maxWidth: '45%'
                }
              })}
              variant="COsubheadL700"
            >
              {fieldAwardHeader}
            </FlourishedHeading>
          )}
          <Box
            css={(theme: LucidTheme) => ({
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              marginTop: theme.space[6],
              marginBottom: theme.space[6]
            })}
            gridColumn="1 / -1"
          >
            <Awards articles={awardsArticles} />
          </Box>
        </Box>
        {fieldFlexiContact && (
          <Box
            css={(theme: LucidTheme) => ({
              flex: '0 1 100%',
              maxWidth: '100%',
              [mqMin.tabletLandscape]: {
                marginLeft: theme.space[4],
                marginRight: theme.space[4]
              }
            })}
          >
            <FlexiStyledMultiSections entity={fieldFlexiContact!.entity!} />
          </Box>
        )}
      </Container>
      {fieldFlexiPrefooterHeading && fieldFlexiPrefooterLink && (
        <Box
          css={(theme: LucidTheme) => ({
            backgroundColor: theme.colors.blueSteel,
            textAlign: 'center',
            padding: theme.space[10]
          })}
        >
          <Heading
            as="h2"
            css={(theme: LucidTheme) => ({
              color: theme.colors.white,
              marginTop: 0,
              marginBottom: theme.space[7]
            })}
            variant="COsubheadS700"
          >
            {fieldFlexiPrefooterHeading}
          </Heading>
          <Link
            displayAsButton
            css={(theme: LucidTheme) => ({
              label: 'preFooter',
              fontWeight: theme.fontWeights.subheading
            })}
            to={fieldFlexiPrefooterLink?.url?.path || ''}
            variant="secondary"
          >
            {fieldFlexiPrefooterLink.title}
          </Link>
        </Box>
      )}
    </Layout>
  );
};

export default FlexiArticleHub;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_newsroom_all
            mktg_resource_center_no_results
            mktg_show_more
            mktg_show_less
          }
        }
      }
    }
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeFlexiArticleHub {
          fieldHeader
          fieldPressReleaseHeader
          fieldAwardHeader
          fieldFlexiContact {
            entity {
              ...flexiStyledMultiSections
            }
          }
          fieldFlexiPrefooterHeading
          fieldFlexiPrefooterLink {
            url {
              path
            }
            title
          }
        }
      }
      newsroom: nodeQuery(
        limit: 1000
        filter: {
          conditions: [
            { field: "type", value: "news", operator: EQUAL }
            { field: "status", value: "1", operator: EQUAL }
          ]
        }
        sort: [
          { field: "sticky", direction: DESC }
          { field: "field_date.value", direction: DESC }
          { field: "changed", direction: DESC }
        ]
      ) {
        entities {
          ... on Drupal_NodeNews {
            fieldDate {
              value
              date
            }
            fieldImageLink
            fieldPrProduct
            fieldPublisher
            fieldSourceUrl
            title
          }
        }
      }
      awards: nodeQuery(
        limit: 1000
        filter: {
          conditions: [
            { field: "type", value: "awards", operator: EQUAL }
            { field: "status", value: "1", operator: EQUAL }
          ]
        }
        sort: [
          { field: "sticky", direction: DESC }
          { field: "field_date.value", direction: DESC }
          { field: "changed", direction: DESC }
        ]
      ) {
        entities {
          ... on Drupal_NodeAwards {
            fieldImageLink
            fieldPublisher
            fieldSourceUrl
            title
          }
        }
      }
      pressReleases: nodeQuery(
        limit: 1000
        filter: {
          conditions: [
            { field: "type", value: "press_releases", operator: EQUAL }
            { field: "status", value: "1", operator: EQUAL }
          ]
        }
        sort: [
          { field: "sticky", direction: DESC }
          { field: "field_date.value", direction: DESC }
          { field: "changed", direction: DESC }
        ]
      ) {
        entities {
          ... on Drupal_NodePressReleases {
            fieldDate {
              value
            }
            fieldPrProduct
            fieldSourceUrl
            title
          }
        }
      }
    }
  }
`;
